import { useState, useRef, useEffect } from "react";
import { Platform, Keyboard } from "react-native";
import { useAuth0 } from "@auth0/auth0-react";
import { NavigationScreenName } from "@lib/utilities/Constants";
import { isValidPassword } from "@lib/utilities/Validators";
import { clearSessionAndLogout } from "@lib/utilities/AuthHelper";

import CheckSession from "@lib/utilities/CheckSession";
import { fetchUserEmail } from "@lib/utilities/HelperFunctions";

import { resetPasswordApi } from "@lib";
import { introspectionAPI } from "@lib";
import { messages } from "../../../src/resources/strings";
import { useAppDispatch, useAppSelector } from "@lib";
import { toogleLoading } from "@lib";
import { resetPasswordHandler } from "@lib";

export const useResetPassword = (navigation: any) => {
  const [errorHandle, setErrorHandle] = useState(messages.error.noError);
  const [isModalVisible, setModalVisible] = useState(false);
  const [email, setEmail] = useState(``);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");

  const [isPasswordResetSuccess, setPasswordResetSuccess] = useState(false);
  const [isFinalSubmit, setFinalSubmit] = useState(false);

  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showCurrentPasswordLabel, setShowCurrentPasswordLabel] =
    useState("Show");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showNewPasswordLabel, setShowNewPasswordLabel] = useState("Show");
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showConfirmPasswordLabel, setShowConfirmPasswordLabel] =
    useState("Show");

  const [currentPasswordHelpText, setCurrentPasswordHelpText] = useState("");
  const [newPasswordHelpText, setNewPasswordHelpText] = useState("");
  const [confirmNewPasswordHelpText, setConfirmNewPasswordHelpText] =
    useState("");

  const currentPasswordRef = useRef();
  const newPasswordRef = useRef();
  const confirmNewPasswordRef = useRef();

  const { isConnected } = useAppSelector((state) => state.userReducer);
  const { logout } = useAuth0();
  const isLoading = useAppSelector((state) => state.policyReducer.isLoading);

  const { resetPassword } = resetPasswordHandler({
    setErrorMessage: setErrorHandle,
    passwordObj: {
      currentPassword,
      newPassword,
      confirmPassword: confirmNewPassword,
    },
    setPasswordResetSuccess,
  });

  const checkSession = async () => {
    const session = await CheckSession();
    if (session) {
      const userEmail = await fetchUserEmail();
      if (userEmail) {
        setEmail(userEmail);
      }
    } else {
      handleInvalidTokenCase();
    }
  };

  const validateCurrentPassword = (value: string, showHelpText: boolean) => {
    setCurrentPassword(value);
    setCurrentPasswordHelpText("");
    if (value === "") {
      showHelpText &&
        setCurrentPasswordHelpText(messages.validation.passcodeRequired);
      return false;
    } else if (value.length < 8 || value.length > 20) {
      showHelpText &&
        setCurrentPasswordHelpText(messages.validation.passcodeLengthMessage);
      return false;
    } else if (isValidPassword(value) == false) {
      showHelpText &&
        setCurrentPasswordHelpText(messages.validation.invalidPasscodeFormat);
      return false;
    }
    return true;
  };

  const validateNewPassword = (value: string, showHelpText: boolean) => {
    setNewPassword(value);
    setNewPasswordHelpText("");
    if (value === "") {
      showHelpText &&
        setNewPasswordHelpText(messages.validation.passcodeRequired);
      return false;
    } else if (value.length < 8 || value.length > 20) {
      showHelpText &&
        setNewPasswordHelpText(messages.validation.passcodeLengthMessage);
      return false;
    } else if (isValidPassword(value) == false) {
      showHelpText &&
        setNewPasswordHelpText(messages.validation.invalidPasscodeFormat);
      return false;
    }
    return true;
  };

  const matchPasswords = (confirmNewPasswordValue: string) => {
    // setConfirmNewPassword(confirmNewPasswordValue);
    const isCurrentPasswordValid = validateCurrentPassword(
      currentPassword,
      true
    );
    const isNewPasswordValid = validateNewPassword(newPassword, true);

    if (!isNewPasswordValid || !isCurrentPasswordValid) {
      return false;
    }
    if (confirmNewPasswordValue === "") {
      setConfirmNewPasswordHelpText(messages.validation.passcodeRequired);
      return false;
    } else if (newPassword === confirmNewPasswordValue) {
      setConfirmNewPasswordHelpText("");
      return true;
    }
    setConfirmNewPasswordHelpText(messages.validation.codeMismatch);
    return false;
  };

  const togglePasswordVisiblity = (passwordType: number, value: boolean) => {
    if (passwordType == 0) {
      setShowCurrentPassword(value);
      setShowCurrentPasswordLabel(value ? "Hide" : "Show");
    } else if (passwordType == 1) {
      setShowNewPassword(value);
      setShowNewPasswordLabel(value ? "Hide" : "Show");
    } else if (passwordType == 2) {
      setShowConfirmPassword(value);
      setShowConfirmPasswordLabel(value ? "Hide" : "Show");
    }
  };

  const showInternetalert = () => {
    setErrorHandle(messages.error.noError);
    setTimeout(() => {
      setErrorHandle(messages.error.youAreOffline);
    }, 10);
  };

  const handleInvalidTokenCase = () => {
    if (isConnected == null || isConnected) {
      clearSessionAndLogout(navigation, logout);
    } else {
      showInternetalert();
    }
  };

  const resetPasswordAction = async () => {
    if (isConnected) {
      setFinalSubmit(true);
      const result = matchPasswords(confirmNewPassword);
      if (result) {
        resetPassword();
      }
    } else {
      showInternetalert();
    }
  };

  const forgotPasswordAction = async () => {
    if (isConnected) {
      navigation.navigate(NavigationScreenName.forgotPassword, {
        email: email,
      });
    } else {
      showInternetalert();
    }
  };

  const toggleModal = () => {
    setModalVisible(!isModalVisible);
  };

  const keyboardAvoidingBehaviour = () => {
    return Platform.OS === "web" || Platform.OS === "ios"
      ? "position"
      : undefined;
  };

  return {
    states: {
      isLoading,
      errorHandle,
      isModalVisible,
      email,
      currentPassword,
      newPassword,
      confirmNewPassword,
      isPasswordResetSuccess,
      isFinalSubmit,
      showCurrentPassword,
      showCurrentPasswordLabel,
      showNewPassword,
      showNewPasswordLabel,
      showConfirmPassword,
      showConfirmPasswordLabel,
      currentPasswordHelpText,
      newPasswordHelpText,
      confirmNewPasswordHelpText,
    },
    refs: {
      currentPasswordRef,
      newPasswordRef,
      confirmNewPasswordRef,
    },
    actions: {
      checkSession,
      setEmail,
      validateCurrentPassword,
      validateNewPassword,
      matchPasswords,
      togglePasswordVisiblity,
      resetPasswordAction,
      forgotPasswordAction,
      toggleModal,
      keyboardAvoidingBehaviour,
      setErrorHandle,
      showInternetalert,
      handleInvalidTokenCase,
      setConfirmNewPassword,
    },
  };
};
